// =============================================================================
// Combobox Component
// =============================================================================
// Searchable dropdown with single/multi selection and optional inline create.
// No external dependencies — same portal + fixed-positioning approach as
// DropdownMenu so the panel is never clipped by overflow:hidden ancestors.
//
// Usage (single):
//   <Combobox value={categoryId} onChange={setCategoryId} options={cats} />
//
// Usage (multi + inline create):
//   <Combobox
//     multiple
//     value={tagIds}
//     onChange={setTagIds}
//     options={tags}
//     onCreate={async (name) => { const t = await createTag(name); return { value: t.id, label: t.name }; }}
//   />
// =============================================================================

import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

export interface ComboboxOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface ComboboxBaseProps {
  options: ComboboxOption[];
  /** Field label rendered above the control. */
  label?: string;
  /** Shown in the control when nothing is selected. */
  placeholder?: string;
  /** Shown in the search input. @default 'Search…' */
  searchPlaceholder?: string;
  disabled?: boolean;
  /** External loading state (e.g. options query in flight). */
  loading?: boolean;
  /**
   * Inline create: rendered as a "Create 'x'" row when the query has no exact
   * (case-insensitive) label match. Must resolve to the created option, which
   * is selected immediately. Errors are surfaced by the caller (toast) — the
   * combobox only clears its busy state.
   */
  onCreate?: (name: string) => Promise<ComboboxOption> | ComboboxOption;
  /** Message when the filtered list is empty. @default 'No matches' */
  emptyMessage?: string;
  /** Compact control (used inline in table rows / bulk bars). */
  size?: 'sm' | 'md';
  className?: string;
}

export interface ComboboxSingleProps extends ComboboxBaseProps {
  multiple?: false;
  value: string | null;
  onChange: (value: string | null) => void;
  /** Show a clear (×) affordance when a value is selected. @default true */
  allowClear?: boolean;
}

export interface ComboboxMultiProps extends ComboboxBaseProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
}

export type ComboboxProps = ComboboxSingleProps | ComboboxMultiProps;

export function Combobox(props: ComboboxProps) {
  const {
    options,
    label,
    placeholder = 'Select…',
    searchPlaceholder = 'Search…',
    disabled = false,
    loading = false,
    onCreate,
    emptyMessage = 'No matches',
    size = 'md',
    className,
  } = props;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const [creating, setCreating] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [position, setPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
  }>({ left: 0, width: 0 });

  const selectedValues = useMemo(
    () => (props.multiple ? props.value : props.value ? [props.value] : []),
    [props.multiple, props.value],
  );
  const byValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  // Inline-create row: only when there is a query with no exact label match.
  const canCreate = Boolean(
    onCreate &&
      query.trim() &&
      !options.some((o) => o.label.toLowerCase() === query.trim().toLowerCase()),
  );
  const rowCount = filtered.length + (canCreate ? 1 : 0);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setHighlighted(0);
  }, []);

  // Position the portal panel relative to the trigger, matching its width.
  // Flips upward when the space below the trigger cannot fit the panel and
  // there is more room above (e.g. triggers inside the fixed bottom bulk bar).
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // Worst-case panel height: search row (~50px) + max-h-56 list (224px).
    const PANEL_MAX_HEIGHT = 290;
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < PANEL_MAX_HEIGHT && rect.top > spaceBelow;
    setPosition(
      flipUp
        ? { bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width }
        : { top: rect.bottom + 4, left: rect.left, width: rect.width },
    );
  }, [open]);

  // Focus the search input when the panel opens.
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  const pick = (value: string) => {
    if (props.multiple) {
      const next = props.value.includes(value)
        ? props.value.filter((v) => v !== value)
        : [...props.value, value];
      props.onChange(next);
      // Multi keeps the panel open for further picks; reset the query so the
      // full list is visible again after each pick.
      setQuery('');
      searchRef.current?.focus();
    } else {
      props.onChange(value);
      close();
    }
  };

  const runCreate = async () => {
    if (!onCreate || creating) return;
    const name = query.trim();
    if (!name) return;
    setCreating(true);
    try {
      const created = await onCreate(name);
      pick(created.value);
    } finally {
      setCreating(false);
    }
  };

  const onSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, rowCount - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlighted < filtered.length) {
        const opt = filtered[highlighted];
        if (opt && !opt.disabled) pick(opt.value);
      } else if (canCreate) {
        void runCreate();
      }
    }
  };

  const removeChip = (value: string) => {
    if (props.multiple) props.onChange(props.value.filter((v) => v !== value));
  };

  const controlPad = size === 'sm' ? 'min-h-[2rem] px-2 py-1 text-xs' : 'min-h-[2.375rem] px-3 py-1.5 text-sm';

  const renderTriggerContent = (): ReactNode => {
    if (props.multiple) {
      if (!selectedValues.length) {
        return <span className="text-text-tertiary">{placeholder}</span>;
      }
      return (
        <span className="flex flex-wrap items-center gap-1">
          {selectedValues.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded bg-brand-500/10 px-1.5 py-0.5 text-xs text-brand-600"
            >
              {byValue.get(v)?.label ?? v}
              {/* span+role, not <button>: the trigger itself is a button and
                  buttons cannot nest. */}
              <span
                role="button"
                tabIndex={0}
                aria-label={`Remove ${byValue.get(v)?.label ?? v}`}
                className="cursor-pointer text-brand-600/70 hover:text-brand-600"
                onClick={(e) => {
                  e.stopPropagation();
                  removeChip(v);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    removeChip(v);
                  }
                }}
              >
                ×
              </span>
            </span>
          ))}
        </span>
      );
    }
    const current = props.value ? (byValue.get(props.value)?.label ?? props.value) : null;
    return current ? (
      <span className="truncate text-text-primary">{current}</span>
    ) : (
      <span className="text-text-tertiary">{placeholder}</span>
    );
  };

  const showClear = !props.multiple && (props.allowClear ?? true) && Boolean(props.value) && !disabled;

  return (
    <div className={clsx('w-full', className)}>
      {label && (
        <span className="mb-1.5 block text-sm font-medium text-text-primary">{label}</span>
      )}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        className={clsx(
          'flex w-full items-center justify-between gap-2 rounded-md border text-left',
          'bg-surface transition-colors duration-150',
          'focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500',
          'disabled:cursor-not-allowed disabled:bg-surface-secondary disabled:opacity-50',
          'border-border',
          controlPad,
        )}
      >
        <span className="min-w-0 flex-1">{renderTriggerContent()}</span>
        <span className="flex shrink-0 items-center gap-1">
          {showClear && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear selection"
              className="cursor-pointer px-0.5 text-text-tertiary hover:text-text-primary"
              onClick={(e) => {
                e.stopPropagation();
                (props as ComboboxSingleProps).onChange(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  (props as ComboboxSingleProps).onChange(null);
                }
              }}
            >
              ×
            </span>
          )}
          <svg
            className="h-4 w-4 text-text-tertiary"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            data-combobox-portal
            role="listbox"
            className="rounded-lg border border-border bg-surface shadow-lg"
            style={{
              position: 'fixed',
              top: position.top,
              bottom: position.bottom,
              left: position.left,
              width: position.width,
              minWidth: 200,
              zIndex: 9999,
            }}
          >
            <div className="border-b border-border p-2">
              <input
                ref={searchRef}
                type="text"
                value={query}
                placeholder={searchPlaceholder}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setHighlighted(0);
                }}
                onKeyDown={onSearchKeyDown}
                className="w-full rounded border border-border bg-surface px-2 py-1 text-sm text-text-primary focus:border-brand-500 focus:outline-none"
              />
            </div>
            <div className="max-h-56 overflow-y-auto py-1">
              {loading && (
                <p className="px-3 py-2 text-sm text-text-tertiary">Loading…</p>
              )}
              {!loading &&
                filtered.map((opt, i) => {
                  const isSelected = selectedValues.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      disabled={opt.disabled}
                      onMouseEnter={() => setHighlighted(i)}
                      onClick={() => !opt.disabled && pick(opt.value)}
                      className={clsx(
                        'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm',
                        opt.disabled && 'cursor-not-allowed opacity-50',
                        !opt.disabled && highlighted === i && 'bg-surface-secondary',
                        isSelected ? 'font-medium text-brand-600' : 'text-text-primary',
                      )}
                    >
                      <span className="truncate">{opt.label}</span>
                      {isSelected && (
                        <svg className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path
                            fillRule="evenodd"
                            d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                  );
                })}
              {!loading && canCreate && (
                <button
                  type="button"
                  onMouseEnter={() => setHighlighted(filtered.length)}
                  onClick={() => void runCreate()}
                  disabled={creating}
                  className={clsx(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-brand-600',
                    highlighted === filtered.length && 'bg-surface-secondary',
                    creating && 'cursor-wait opacity-60',
                  )}
                >
                  <span className="text-base leading-none">+</span>
                  {creating ? 'Creating…' : `Create “${query.trim()}”`}
                </button>
              )}
              {!loading && filtered.length === 0 && !canCreate && (
                <p className="px-3 py-2 text-sm text-text-tertiary">{emptyMessage}</p>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
